import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { RolesEnum } from "@/lib/types/roles.types";
import type { DesktopEntityTypeWithSystems } from "@/lib/supabase/supabaseTypes";

function formatInstallTime(d: Date): string {
	const isToday =
		d.getDate() === new Date().getDate() &&
		d.getMonth() === new Date().getMonth() &&
		d.getFullYear() === new Date().getFullYear();
	const timeStr = d.toLocaleTimeString("de-CH", {
		hour: "2-digit",
		minute: "2-digit",
	});
	if (isToday) return `um ${timeStr}`;
	const dateStr = d.toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
	});
	return `am ${dateStr} um ${timeStr}`;
}

function getInstallTime(desktop: DesktopEntityTypeWithSystems): string {
	const postponeTo = desktop.postpone_update_to;
	const downloadedAt = desktop.downloaded_at;
	const iso =
		postponeTo ??
		(downloadedAt
			? new Date(
					new Date(downloadedAt).getTime() + 5 * 60 * 1000
				).toISOString()
			: null);
	if (!iso) return "";
	const d = new Date(iso);
	if (d.getTime() <= Date.now()) return "wird installiert…";
	return formatInstallTime(d);
}

interface DesktopUpdateBannerProps {
	expanded: boolean;
}

export const DesktopUpdateBanner: React.FC<DesktopUpdateBannerProps> = ({
	expanded,
}) => {
	const navigate = useNavigate();
	const role = useCentralStore((state) => state.role);
	const desktopsWithSystems = useDesktopsStore(
		(state) => state.desktopsWithSystems
	);

	if (role === null || role > RolesEnum.ADMIN) return null;

	const updatingDesktops = desktopsWithSystems.filter(
		(d) => d.update_version
	);
	if (updatingDesktops.length === 0) return null;

	return (
		<div className="flex-shrink-0 px-2 py-1">
			{updatingDesktops.map((desktop) => {
				const timeText = getInstallTime(desktop);
				return (
					<div
						key={desktop.id}
						className={cn(
							"flex items-center gap-2 py-1.5 pl-2 pr-2 rounded-md border cursor-pointer transition-colors",
							"bg-blue-50 border-blue-200 hover:bg-blue-100"
						)}
						onClick={() =>
							navigate("/organisation/administration/drucker")
						}
						title={`Update v${desktop.update_version} für ${desktop.name}`}
						aria-label="Desktop-Update-Hinweis"
						data-testid={`desktop-update-banner-${desktop.id}`}
					>
						<RefreshCw className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
						{expanded && (
							<div className="text-[11px] text-blue-700">
								Druckservice Update v{desktop.update_version}{" "}
								{timeText}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};
